import { CommonModule } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { AnimationOptions } from 'ngx-lottie'
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { library } from 'src/app/ngrx/store/actions'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { BookmarkService } from 'src/app/service/data/bookmark.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { BookmarkStateService } from 'src/app/service/state/bookmark-state.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { DdcContentCardComponent } from 'src/app/shared/ddc/ddc-content-card/ddc-content-card.component'
import { DdcDeleteDialogComponent } from 'src/app/shared/ddc/ddc-delete-dialog/ddc-delete-dialog.component'
import { DdcIconFunctionComponent } from 'src/app/shared/ddc/ddc-delete/ddc-icon-function.component'
import { DdcDesktopDialogComponent } from 'src/app/shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcLoadingSkeletonComponent } from 'src/app/shared/ddc/ddc-loading-skeleton/ddc-loading-skeleton.component'
import { DdcMobileSheetComponent } from 'src/app/shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import { DdcNotAvailableComponent } from 'src/app/shared/ddc/ddc-not-available/ddc-not-available.component'
import { DdcPairDialogComponent } from 'src/app/shared/ddc/ddc-pair-dialog/ddc-pair-dialog.component'
import { DdcWordDialogComponent } from 'src/app/shared/ddc/ddc-word-dialog/ddc-word-dialog.component'
import { ContentTypes } from 'src/app/shared/enums/enums'
import { CollectionItem } from 'src/app/shared/models/main/collection'
import { DdcButtonNOneComponent } from '../../shared/ddc/ddc-button-n-one/ddc-button-n-one.component'
import { DdcTextGradientComponent } from '../../shared/ddc/ddc-text-gradient/ddc-text-gradient.component'
import { DeleteWordPairDicCommand } from '../commands/delete-word-pair-dic/delete-word-pair-dic.command'

@Component({
  selector: 'ddc-collection-page',
  templateUrl: './ddc-collection-page.component.html',
  styleUrl: './ddc-collection-page.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcTextGradientComponent,
    DdcButtonNOneComponent,
    InfiniteScrollDirective,
    DdcNotAvailableComponent,
    DdcLoadingSkeletonComponent,
    DdcContentCardComponent,
    DdcIconFunctionComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcCollectionPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  _collectionTitle: string = 'All Items'

  _allItemsMode: boolean = true
  _collectionId: number = null
  _collectionName: string = null
  _isMobile: boolean = false
  _theme$: Observable<string>
  _subscription: string = 'Free Tier'

  _elems: CollectionItem[] = []
  _isElemsLoading: boolean = false
  _elemsPageInfoLabel = 'Scroll down to load more'
  _elemsScrollLoading = false
  _elemsEnd = false
  _fetchElemsError: string = 'Empty.'
  _isElemsEmpty: boolean = false
  _nElems: number
  _nElemsStr: string = '0'

  scroll_options: AnimationOptions = {
    path: '/assets/lottie/scroll_down.json',
    loop: true, // Set loop to false for a single playthrough
    autoplay: true,
  }

  loading_options: AnimationOptions = {
    path: '/assets/lottie/loading2.json',
    loop: true, // Set loop to false for a single playthrough
    autoplay: true,
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private logger: DdcLoggerService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private user_info: UserService,
    public bookmark_service: BookmarkService,
    private bookmarkStateService: BookmarkStateService,
    private route: ActivatedRoute,
    private deleteWordPairDicCommand: DeleteWordPairDicCommand,
  ) {
    this._theme$ = this.store.select(getTheme)
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 1224
  }
  checkIfMobile(): void {
    if (window.innerWidth < 500) {
      // 768px is a common breakpoint for mobile devices
      this._isMobile = true
    } else {
      this._isMobile = false
    }
  }
  ngOnInit(): void {
    this.checkIfMobile()
    this.store.dispatch(library())

    //get if user is subscribed from this.user_info.userInfo$
    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe(userInfo => {
      this._subscription = userInfo?.subscription || 'Free Tier'
    })

    this.route.params.subscribe(params => {
      const param_coll_id = params['coll_id']

      this.getCollectionInfo(param_coll_id)
      const param_coll_name = params['coll_name']

      if (param_coll_id == 'all_items') {
        this._allItemsMode = true
        this._collectionTitle = 'All Items'
        this._collectionId = null
        this.setupAllItemsSubscriptions()
      } else {
        this._collectionId = Number(param_coll_id)
        this._collectionName = param_coll_name
        this._allItemsMode = false
        this.setupCollectionSubscriptions(this._collectionId)
      }
    })

    // Load initial data
    this.loadInitialData()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private setupAllItemsSubscriptions(): void {
    // Subscribe to all items data
    combineLatest([
      this.bookmarkStateService.allItems$,
      this.bookmarkStateService.allItemsTotal$,
      this.bookmarkStateService.allItemsEnd$,
      this.bookmarkStateService.allItemsLoading$,
      this.bookmarkStateService.allItemsError$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([items, total, end, loading, error]) => {
        this._elems = items
        this._nElems = total
        this._nElemsStr = total.toString()
        this._elemsEnd = end
        // Only show full skeleton on initial load (no items yet)
        this._isElemsLoading = loading && items.length === 0
        // Show inline scroll loading when loading more items
        this._elemsScrollLoading = loading && items.length > 0

        if (error) {
          this._fetchElemsError = error
          this._isElemsEmpty = true
        } else {
          this._isElemsEmpty = false
        }

        this.updatePageInfoLabel()
      })
  }

  private setupCollectionSubscriptions(collectionId: number): void {
    // Subscribe to collection-specific data
    combineLatest([
      this.bookmarkStateService.getCollectionItems$(collectionId),
      this.bookmarkStateService.getCollectionTotal$(collectionId),
      this.bookmarkStateService.getCollectionEnd$(collectionId),
      this.bookmarkStateService.getCollectionLoading$(collectionId),
      this.bookmarkStateService.getCollectionError$(collectionId),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([items, total, end, loading, error]) => {
        this._elems = items
        this._nElems = total
        this._nElemsStr = total.toString()
        this._elemsEnd = end
        // Only show full skeleton on initial load (no items yet)
        this._isElemsLoading = loading && items.length === 0
        // Show inline scroll loading when loading more items
        this._elemsScrollLoading = loading && items.length > 0

        if (error) {
          this._fetchElemsError = error
          this._isElemsEmpty = true
        } else {
          this._isElemsEmpty = false
        }

        this.updatePageInfoLabel()
      })

    // Subscribe to bookmark groups to detect collection name changes
    this.bookmarkStateService.bookmarkGroups$.pipe(takeUntil(this.destroy$)).subscribe(groups => {
      const currentCollection = groups.find(group => group.id === Number(collectionId))
      if (currentCollection && currentCollection.name !== this._collectionTitle) {
        this._collectionTitle = currentCollection.name
        this._collectionName = currentCollection.name
      }
    })
  }

  openDeleteCollectionDialog(): void {
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: 'Collection',
        element: { id: this._collectionId, name: this._collectionName },
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        // Set the command arguments
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: undefined,
          object_id: undefined,
          collection_id: this._collectionId,
        })

        // Execute the delete command
        this.deleteWordPairDicCommand.execute().subscribe(() => {
          this.router.navigate(['/saved'])
        })
      }
    })
  }
  private updatePageInfoLabel(): void {
    if (this._isElemsLoading || this._elemsScrollLoading) {
      this._elemsPageInfoLabel = 'Loading...'
    } else if (this._elemsEnd) {
      this._elemsPageInfoLabel = 'All contents loaded.'
    } else {
      this._elemsPageInfoLabel = 'Scroll down to load more'
    }
  }

  private async loadInitialData(): Promise<void> {
    try {
      // Always load bookmark groups to ensure we have collection metadata
      await this.bookmarkStateService.loadBookmarkGroups()

      if (this._allItemsMode) {
        await this.bookmarkStateService.loadAllItems(true)
      } else if (this._collectionId) {
        await this.bookmarkStateService.loadCollectionItems(this._collectionId, true)
      }
    } catch (error) {
      this.logger.error('DdcCollectionPageComponent | loadInitialData | error:', error)
    }
  }

  getCollectionInfo(id: any): void {
    this.logger.info('DdcCollectionPageComponent | getCollectionInfo() | id:', id)

    if (id !== 'all_items') {
      this.bookmark_service.get_bookmark_group(id).subscribe((res: any) => {
        if (res) {
          this._collectionName = res.name
          this._collectionTitle = this._collectionName
        }
      })
    }
  }

  onElemsScroll(): void {
    if (this._elemsScrollLoading || this._isElemsLoading || this._elemsEnd) {
      return
    }
    this.logger.info('DdcCollectionPageComponent | onElemsScroll()')

    if (this._allItemsMode) {
      this.bookmarkStateService.loadMoreAllItems()
    } else if (this._collectionId) {
      this.bookmarkStateService.loadMoreCollectionItems(this._collectionId)
    }
  }

  openEditCollectionDialog(): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()

      // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
      let themeClass = ''
      this._theme$
        .subscribe(theme => {
          themeClass = theme
        })
        .unsubscribe()
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = {
        type: 'edit-collection',
        element: { id: this._collectionId, name: this._collectionName },
      }
      const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
      dialogRef.afterClosed().subscribe(result => {
        // Collection name should be automatically updated via bookmark groups subscription
        // Optionally reload bookmark groups to ensure we have the latest data
        this.bookmarkStateService.loadBookmarkGroups()
      })
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = {
        type: 'edit-collection',
        element: { id: this._collectionId, name: this._collectionName },
      }
      const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
      dialogRef.afterClosed().subscribe(result => {
        // Collection name should be automatically updated via bookmark groups subscription
        // Optionally reload bookmark groups to ensure we have the latest data
        this.bookmarkStateService.loadBookmarkGroups()
      })
    }
  }

  openReadWordDialog(element: any): void {
    this.logger.info('DdcCollectionPageComponent | openReadWordDialog() | element:', element)

    const dialogRef = this.dialog.open(DdcWordDialogComponent, {
      data: {
        element: element,
        read_only: true,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'word') {
        // Handle any word-related updates here if needed
      }
    })
  }
  openEditWordDialog(index: number): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()

      // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
      let themeClass = ''
      this._theme$
        .subscribe(theme => {
          themeClass = theme
        })
        .unsubscribe()
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = { type: 'edit-word', element: this._elems[index] }
      const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type: 'edit-word', element: this._elems[index] }
      const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'word') {
        }
      })
    }
  }

  openDeletePairDialog(index: number): void {
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: 'Pair',
        element: this._elems[index],
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        // Set the command arguments
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.PAIR,
          object_id: this._elems[index].id,
        })

        // Execute the delete command
        this.deleteWordPairDicCommand.execute().subscribe(() => {
          // Remove from local array
          const indexToRemove = this._elems.findIndex(elem => elem.id === this._elems[index].id)
          if (indexToRemove !== -1) {
            // Force change detection
            this.cdr.detectChanges()
          }
        })
      }
    })
  }
  openEditPairDialog(index: number): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()

      // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
      let themeClass = ''
      this._theme$
        .subscribe(theme => {
          themeClass = theme
        })
        .unsubscribe()
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = { type: 'edit-pair', element: this._elems[index] }
      const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type: 'edit-pair', element: this._elems[index] }
      const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'pair') {
        }
      })
    }
  }

  openDeleteDicDialog(index: number): void {
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: 'Dictionary',
        element: this._elems[index],
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        // Set the command arguments
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.DIC,
          object_id: this._elems[index].id,
        })

        // Execute the delete command
        this.deleteWordPairDicCommand.execute().subscribe(() => {
          // Remove from local array
          const indexToRemove = this._elems.findIndex(elem => elem.id === this._elems[index].id)
          if (indexToRemove !== -1) {
            // Force change detection
            this.cdr.detectChanges()
          }
        })
      }
    })
  }
  openDeleteWordDialog(index: number): void {
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: 'Word',
        element: this._elems[index],
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        // Set the command arguments
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.WORD,
          object_id: this._elems[index].id,
        })

        // Execute the delete command
        this.deleteWordPairDicCommand.execute().subscribe(() => {
          // Remove from local array
          const indexToRemove = this._elems.findIndex(elem => elem.id === this._elems[index].id)
          if (indexToRemove !== -1) {
            // Force change detection
            this.cdr.detectChanges()
          }
        })
      }
    })
  }

  openReadPairDialog(element: any): void {
    this.logger.info('DdcCollectionPageComponent | openReadPairDialog() | element:', element)

    const dialogRef = this.dialog.open(DdcPairDialogComponent, {
      data: {
        element: element,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'pair') {
        // Handle any pair-related updates here if needed
      }
    })
  }

  // Method to handle bookmark additions from other components
  addBookmarkItem(item: CollectionItem): void {
    this.bookmarkStateService.addBookmarkItem(
      item,
      this._allItemsMode ? undefined : this._collectionId,
    )
  }

  onBookmarkChanged(isBookmarked: boolean, index: number): void {
    if (!isBookmarked) {
      const item = this._elems[index]
      this.bookmarkStateService.removeBookmarkItem(
        item.id,
        item.type,
        this._allItemsMode ? undefined : this._collectionId,
      )
    }
  }

  // Method to handle bookmark updates from other components
  updateBookmarkItem(item: CollectionItem): void {
    this.bookmarkStateService.updateBookmarkItem(item)
  }

  // Method to handle bookmark removals from other components
  removeBookmarkItem(itemId: number, itemType: string): void {
    this.bookmarkStateService.removeBookmarkItem(
      itemId,
      itemType,
      this._allItemsMode ? undefined : this._collectionId,
    )
  }

  // Note dialogs
  openReadNoteDialog(element: any): void {
    this.logger.info('DdcCollectionPageComponent | openReadNoteDialog() | element:', element)
    const type = 'read-note'
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openDeleteNoteDialog(index: number): void {
    const element = this._elems[index]
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: { type: 'Note', element },
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.NOTE,
          object_id: element.id,
        })
        this.deleteWordPairDicCommand.execute().subscribe(() => this.cdr.detectChanges())
      }
    })
  }

  // Quiz dialogs
  openReadQuizDialog(element: any): void {
    this.logger.info('DdcCollectionPageComponent | openReadQuizDialog() | element:', element)
    const type = 'read-quiz'
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openDeleteQuizDialog(index: number): void {
    const element = this._elems[index]
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: { type: 'Quiz', element },
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.QUIZ,
          object_id: element.id,
        })
        this.deleteWordPairDicCommand.execute().subscribe(() => this.cdr.detectChanges())
      }
    })
  }

  // Figure dialogs
  openReadFigureDialog(element: any): void {
    this.logger.info('DdcCollectionPageComponent | openReadFigureDialog() | element:', element)
    const type = 'read-figure'
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openDeleteFigureDialog(index: number): void {
    const element = this._elems[index]
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: { type: 'Figure', element },
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: ContentTypes.FIGURE,
          object_id: element.id,
        })
        this.deleteWordPairDicCommand.execute().subscribe(() => this.cdr.detectChanges())
      }
    })
  }

  private getMobileDialogConfig(type: string, element: any): MatDialogConfig {
    let themeClass = ''
    this._theme$
      .subscribe(theme => {
        themeClass = theme
      })
      .unsubscribe()

    const dialogConfig = new MatDialogConfig()
    dialogConfig.panelClass = ['mobile-sheet-container', themeClass]
    dialogConfig.position = { bottom: '0' }
    dialogConfig.width = '100%'
    dialogConfig.height = '80vh'
    dialogConfig.maxWidth = '100vw'
    dialogConfig.hasBackdrop = true
    dialogConfig.backdropClass = 'mobile-sheet-backdrop'
    dialogConfig.enterAnimationDuration = '300ms'
    dialogConfig.exitAnimationDuration = '200ms'
    dialogConfig.data = { type, element }
    return dialogConfig
  }
}
